import { useCallback } from 'react';

import {
   VisitDraftPrescriptionsQuantity,
   VisitDraftPrescriptionsQuantityQuery,
} from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsGet } from '@dreryk/command-query-react-common';
import {
   ActionBar,
   Badge,
   BadgePosition,
   BadgeType,
   NotesIcon,
   useHistoryPushWithBackAction,
} from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';

import { visitActionBarTranslationKeys, visitTranslationKeys } from '../../translations';

import { VisitActionBarOrder } from './VisitActionBar';

type UnsentPrescriptionsActionBadgeProps = {
   readonly visitId: number;
   readonly children: JSX.Element;
};

export const UnsentPrescriptionsActionBadge = ({
   children,
   visitId,
}: UnsentPrescriptionsActionBadgeProps): JSX.Element => {
   const { data: draftPrescriptionsQuantity } = useCqrsGet<VisitDraftPrescriptionsQuantity>(() => {
      const query = new VisitDraftPrescriptionsQuantityQuery();
      query.visitId = visitId;
      return query;
   });

   const { numberOfDraftPrescriptions = 0 } = draftPrescriptionsQuantity ?? {};

   return (
      <Badge
         type={BadgeType.WARNING}
         count={numberOfDraftPrescriptions}
         position={BadgePosition.TOP_RIGHT}
         testId={'unsentPrescriptions'}
         visible={Boolean(numberOfDraftPrescriptions)}
         compact
      >
         {children}
      </Badge>
   );
};

type GoToPrescriptionActionProps = {
   readonly visitId: number;
};

export const GoToPrescriptionAction = (props: GoToPrescriptionActionProps): JSX.Element => {
   const push = useHistoryPushWithBackAction(false);

   const goToPrescription = useCallback(
      (): void =>
         push(visitPaths.getPrescriptionsPath({ params: { visitId: props.visitId } }), {
            label: visitTranslationKeys.goBackToVisit,
         }),
      [push, props.visitId]
   );

   return (
      <ActionBar.OptionalLink
         testId={'goToPrescription'}
         icon={
            <UnsentPrescriptionsActionBadge visitId={props.visitId}>
               <NotesIcon />
            </UnsentPrescriptionsActionBadge>
         }
         label={visitActionBarTranslationKeys.prescription}
         onClick={goToPrescription}
         order={VisitActionBarOrder.Prescription}
      />
   );
};
